import { isAvailableOn } from 'availability';
import _ from 'lodash';
import { UnavailabilityReason } from '../types/Availability';
var addToArray = function (oldArray, newItem) {
    var copy = _.cloneDeep(oldArray);
    copy.push(newItem);
    return copy;
};
var getUnavailableReason = function (availability) {
    if (availability && availability.exceptions) {
        return availability.exceptions[0].reason || '';
    }
    return '';
};
function evaluateConditionWithDetails(conditions, params, validConditions, invalidConditions) {
    if (validConditions === void 0) { validConditions = []; }
    if (invalidConditions === void 0) { invalidConditions = []; }
    var allResults = conditions.map(function (condition) {
        switch (condition.type) {
            case 'false':
                return {
                    validConditions: validConditions,
                    invalidConditions: addToArray(invalidConditions, {
                        type: condition.type,
                    }),
                };
            case 'true':
                return {
                    validConditions: addToArray(validConditions, {
                        type: condition.type,
                    }),
                    invalidConditions: invalidConditions,
                };
            case 'not':
                var notResult = evaluateConditionWithDetails([condition.condition], params, validConditions, invalidConditions);
                return {
                    validConditions: notResult.invalidConditions,
                    invalidConditions: notResult.validConditions,
                };
            case 'or':
                var orResult = evaluateConditionWithDetails(condition.conditions || [], params, validConditions, invalidConditions);
                return {
                    validConditions: orResult.validConditions,
                    invalidConditions: orResult.invalidConditions.length < condition.conditions.length
                        ? []
                        : orResult.invalidConditions,
                };
            case 'and':
                var andResult = evaluateConditionWithDetails(condition.conditions || [], params, validConditions, invalidConditions);
                return {
                    validConditions: andResult.validConditions,
                    invalidConditions: andResult.invalidConditions.length > 0
                        ? andResult.invalidConditions
                        : [],
                };
            case 'order_delivery_time':
                var isAvailable = isAvailableOn(condition.availability, params.dispatchTime);
                return {
                    validConditions: isAvailable
                        ? addToArray(validConditions, { type: condition.type })
                        : validConditions,
                    invalidConditions: isAvailable
                        ? invalidConditions
                        : addToArray(invalidConditions, {
                            type: condition.type,
                            reason: getUnavailableReason(condition.availability) ||
                                UnavailabilityReason.Unavailable,
                        }),
                };
            case 'order_platform':
                var isPlatformEqual = condition.platform === params.platform;
                return {
                    validConditions: isPlatformEqual
                        ? addToArray(validConditions, {
                            type: condition.type,
                            platform: condition.platform,
                        })
                        : validConditions,
                    invalidConditions: isPlatformEqual
                        ? invalidConditions
                        : addToArray(invalidConditions, {
                            type: condition.type,
                            platform: condition.platform,
                        }),
                };
            case 'order_delivery_type':
                var isDispatchTypeEqual = condition.deliveryType === params.dispatchType;
                return {
                    validConditions: isDispatchTypeEqual
                        ? addToArray(validConditions, {
                            type: condition.type,
                            deliveryType: condition.deliveryType,
                        })
                        : validConditions,
                    invalidConditions: isDispatchTypeEqual
                        ? invalidConditions
                        : addToArray(invalidConditions, {
                            type: condition.type,
                            deliveryType: condition.deliveryType,
                        }),
                };
            case 'order_coupon':
                var isCouponHashCodeMatching = condition.couponHashCode === params.couponHashCode;
                return {
                    validConditions: isCouponHashCodeMatching
                        ? addToArray(validConditions, { type: condition.type })
                        : validConditions,
                    invalidConditions: isCouponHashCodeMatching
                        ? invalidConditions
                        : addToArray(invalidConditions, { type: condition.type }),
                };
            case 'order_items_price':
                var hasCriteria = typeof condition.min === 'number' ||
                    typeof condition.max === 'number';
                var isPriceInRange = typeof params.totalOrderPrice === 'number' &&
                    params.totalOrderPrice >=
                        (typeof condition.min === 'number' ? condition.min : 0) &&
                    params.totalOrderPrice <=
                        (typeof condition.max === 'number'
                            ? condition.max
                            : Number.MAX_SAFE_INTEGER);
                var isPriceValid = isPriceInRange || !hasCriteria;
                return {
                    validConditions: isPriceValid
                        ? addToArray(validConditions, {
                            type: condition.type,
                            min: condition.min,
                        })
                        : validConditions,
                    invalidConditions: isPriceValid
                        ? invalidConditions
                        : addToArray(invalidConditions, {
                            type: condition.type,
                            min: condition.min,
                        }),
                };
            default:
                throw new Error("Unsupported condition type: " + condition.type);
        }
    });
    return {
        validConditions: allResults.flatMap(function (r) { return r.validConditions; }),
        invalidConditions: allResults.flatMap(function (r) { return r.invalidConditions; }),
    };
}
/**
 * Evaluate any condition.
 *
 * @example
 * ```ts
 * let condition: Condition = { type: 'order_platform', platform: 'web' };
 * let params: ConditionParams = { platform: 'web' };
 * const result = evaluateCondition(condition, params);
 * ```
 * `result` will be `{ result: true, reasons: [] }`, since the condition evaluates to `true`.
 *
 * @example
 * ```ts
 * const condition: Condition = { type: 'or', conditions: [
 *    { type: 'order_platform', platform: 'web' },
 *    { type: 'order_platform', platform: 'mobile' },
 *  ]};
 * const params: ConditionParams = { platform: 'callcenter' };
 * const result = evaluateCondition(condition, params);
 * ```
 * `result` will be `{ result: false, reasons: ['order_platform'] }`, since the condition evaluates to false, because of platform mismatch.
 *
 * @param condition - Condition to be evaluated.
 * @param params - Parameters to evaluate condition by.
 *
 * @returns Whether the condition is truthy. In case it's false, stating the reasons.
 */
export function evaluateCondition(condition, params) {
    if (condition === void 0) { condition = { type: 'true' }; }
    var reasons = evaluateConditionWithDetails([condition], params)
        .invalidConditions;
    return {
        result: reasons.length < 1,
        reasons: reasons,
    };
}
export function evaluateConditionResult(condition, params) {
    if (condition === void 0) { condition = { type: 'true' }; }
    var res = evaluateCondition(condition, params);
    return res.result;
}
//# sourceMappingURL=condition.js.map